// ENG-9346: persist the manager's "stuck streak" counters across process
// restarts.
//
// THE BUG THIS EXISTS TO FIX. Two heartbeat fields report agents whose
// background retry loop has not converged: `channel_sync_stuck` (ENG-9252) and
// `realtime_rebind_stuck` (ENG-9265). Both are backed by a module-level `Map`
// in manager-worker.ts, so a manager restart wipes them. The next heartbeat
// then sends an EMPTY array, and the control plane reads absence-from-the-list
// as convergence: `host-runtime.ts` closes the alert with
// `closed_reason: 'recovered'`, on a condition nobody re-evaluated.
//
// Measured on 2026-08-22, two unrelated orgs within two hours of each other.
// `mustard` (dig) and `sterling` (alyve) each had a dead Telegram channel for
// 50+ hours while every other projection read healthy — heartbeat fresh, host
// fresh, EC2 running, auth valid. Once a day the only alert naming the problem
// closed itself as recovered and reopened 10-13 minutes later with the age
// counter restarted.
//
// That 10-13 minute gap is the tell, and it is why this file persists `since`
// rather than merely suppressing the empty array. `CHANNEL_SYNC_STUCK_AFTER_MS`
// is 10 minutes: the reopen delay IS the re-arm threshold, measured from a
// streak that started again at zero. So the wipe cost two distinct things —
// a fabricated recovery, and the loss of the age that would have let anyone
// triaging by duration see a two-day-old outage instead of a fresh one.
//
// WHY NOT FIX IT AT THE API INSTEAD. The wire contract is deliberate and worth
// keeping: the heartbeat ALWAYS sends these arrays, including empty, because
// omitting-when-empty would make "recovered" and "this CLI is too old to
// report" the same value on the wire. The empty array is a genuine affirmative
// all-clear. The defect is that a restarted manager sends one when it has no
// idea whether the condition cleared. Restoring the state restores the
// contract's truth rather than weakening the contract.
//
// SHAPE. Mirrors sender-policy-baseline.ts (ENG-7771/ENG-7779), which exists
// for the identical wipe-on-manager-restart pathology one subsystem over: a
// single versioned JSON object, one key per concern, atomic writes, and a
// corrupt or version-mismatched file discarded wholesale WITH A LOG rather
// than partially applied.
//
// DEGRADATION IS SAFE BY CONSTRUCTION. Every failure path here (missing file,
// corrupt JSON, wrong version, unwritable disk) degrades to exactly today's
// behaviour: an empty map, one spurious `recovered`, and a restarted clock.
// That is the bug, not a new hazard — which is why none of it is fatal. It is
// logged precisely because a silent degradation would be indistinguishable
// from the fix working.

import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';

import { atomicWriteFileSync } from './atomic-write.js';

const STORE_FILENAME = 'stuck-streaks.json';

/**
 * Bump when the meaning of a persisted field changes, so stale sidecars are
 * discarded wholesale instead of being half-understood. A discarded sidecar
 * costs one spurious `recovered` per affected agent — the pre-fix behaviour —
 * which is strictly better than acting on a field that no longer means what it
 * says.
 */
export const STUCK_STREAK_STORE_VERSION = 1;

export const STUCK_STREAK_CONCERNS = ['channelSync', 'realtimeRebind'] as const;
export type StuckStreakConcern = (typeof STUCK_STREAK_CONCERNS)[number];

/**
 * The persisted core of a streak.
 *
 * Deliberately the INTERSECTION of `ChannelSyncFailureState` and
 * `RealtimeRebindState` rather than a union of both. The two differ only in
 * the name of their free-text field (`lastReason` / `lastStatus`), so it is
 * carried here as `detail` and mapped at the two call sites. Persisting a
 * concern-specific shape would have this file grow a branch every time a
 * third caller adopts it.
 */
export interface PersistedStreak {
  /** Consecutive non-convergent attempts. */
  attempts: number;
  /** Epoch ms of the first failure in this streak. The field that matters. */
  since: number;
  /** Epoch ms before which the next attempt is skipped. */
  nextAttemptAt: number;
  /** `attempts` at the last summary line, so log throttling survives too. */
  loggedAtAttempt: number;
  /** Why it last failed: the reason (channelSync) or status (realtimeRebind). */
  detail: string;
}

export type StuckStreakMaps = Record<StuckStreakConcern, Map<string, PersistedStreak>>;

export function createStuckStreakMaps(): StuckStreakMaps {
  return { channelSync: new Map(), realtimeRebind: new Map() };
}

export function getStuckStreakStoreFile(configDir: string): string {
  return join(configDir, STORE_FILENAME);
}

/**
 * Validate one persisted entry.
 *
 * Every numeric field is checked with `Number.isFinite`, not merely
 * `typeof === 'number'`: `JSON.parse` yields `null` for a serialized `NaN` or
 * `Infinity`, but a hand-edited or future-version file can carry either, and a
 * non-finite `since` would make `now - since` non-finite too. The stuck
 * comparison would then be false forever and the agent would silently never be
 * reported again — a failure strictly worse than the one being fixed, because
 * it is permanent rather than daily.
 *
 * `since` and `nextAttemptAt` are epoch milliseconds and must be positive;
 * `attempts` must be at least 1, because an entry recording zero failures is
 * not a streak and would report `stuck_for_seconds` against a streak that never
 * began.
 */
export function isValidPersistedStreak(value: unknown): value is PersistedStreak {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.attempts !== 'number' || !Number.isFinite(v.attempts) || v.attempts < 1) return false;
  if (typeof v.since !== 'number' || !Number.isFinite(v.since) || v.since <= 0) return false;
  if (typeof v.nextAttemptAt !== 'number' || !Number.isFinite(v.nextAttemptAt)) return false;
  if (
    typeof v.loggedAtAttempt !== 'number'
    || !Number.isFinite(v.loggedAtAttempt)
    || v.loggedAtAttempt < 0
  ) return false;
  if (typeof v.detail !== 'string') return false;
  return true;
}

/**
 * Parse a store payload into per-concern maps. Pure, so the discard rules are
 * asserted directly rather than through the filesystem.
 *
 * Returns `null` for a payload that must be discarded WHOLESALE (not an
 * object, or a version mismatch). An individual malformed entry is dropped on
 * its own without taking the rest of the file with it: one unparseable agent
 * should not restart the clock for every other agent on the host.
 */
export function parseStuckStreakPayload(parsed: unknown): StuckStreakMaps | null {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const obj = parsed as Record<string, unknown>;
  if (obj.version !== STUCK_STREAK_STORE_VERSION) return null;
  const maps = createStuckStreakMaps();
  for (const concern of STUCK_STREAK_CONCERNS) {
    const entries = obj[concern];
    if (!entries || typeof entries !== 'object' || Array.isArray(entries)) continue;
    for (const [agentId, entry] of Object.entries(entries as Record<string, unknown>)) {
      if (!agentId) continue;
      if (!isValidPersistedStreak(entry)) continue;
      maps[concern].set(agentId, entry);
    }
  }
  return maps;
}

/** Serialize per-concern maps into the on-disk payload. Pure. */
export function buildStuckStreakPayload(source: StuckStreakMaps): Record<string, unknown> {
  const payload: Record<string, unknown> = { version: STUCK_STREAK_STORE_VERSION };
  for (const concern of STUCK_STREAK_CONCERNS) {
    const entries: Record<string, PersistedStreak> = {};
    for (const [agentId, streak] of source[concern]) entries[agentId] = streak;
    payload[concern] = entries;
  }
  return payload;
}

/**
 * Hydrate `target` from disk.
 *
 * A missing file is silent — that is a fresh host, or the first boot after this
 * ships. Everything else is logged: the whole point of the change is that a
 * restarted manager stops claiming recovery it did not observe, and a silent
 * fallback to the empty map would reinstate exactly that while looking fixed.
 */
export function loadStuckStreaks(
  target: StuckStreakMaps,
  configDir: string,
  log?: (msg: string) => void,
): void {
  const path = getStuckStreakStoreFile(configDir);
  if (!existsSync(path)) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(path, 'utf-8'));
  } catch (err) {
    log?.(
      `[stuck-streaks] discarding corrupt ${STORE_FILENAME} (${(err as Error).message}) — `
        + `stuck-streak ages restart from zero and one spurious "recovered" close may follow`,
    );
    return;
  }
  const maps = parseStuckStreakPayload(parsed);
  if (!maps) {
    log?.(
      `[stuck-streaks] discarding unusable ${STORE_FILENAME} (bad shape or version, expected `
        + `${STUCK_STREAK_STORE_VERSION}) — stuck-streak ages restart from zero and one spurious `
        + `"recovered" close may follow`,
    );
    return;
  }
  for (const concern of STUCK_STREAK_CONCERNS) {
    for (const [agentId, streak] of maps[concern]) target[concern].set(agentId, streak);
  }
}

/**
 * Persist `source`. Returns whether the write landed so the caller can keep a
 * dirty flag and retry — without that, a transiently-failed write leaves the
 * sidecar stale until the next unrelated mutation, which is the CodeRabbit
 * PR #3432 finding on the sibling store.
 */
export function saveStuckStreaks(
  source: StuckStreakMaps,
  configDir: string,
  log?: (msg: string) => void,
): boolean {
  const path = getStuckStreakStoreFile(configDir);
  try {
    atomicWriteFileSync(path, JSON.stringify(buildStuckStreakPayload(source), null, 2));
    return true;
  } catch (err) {
    log?.(
      `[stuck-streaks] failed to persist ${STORE_FILENAME} (non-fatal): ${(err as Error).message}`,
    );
    return false;
  }
}

/**
 * Drop persisted entries for agents this host no longer runs, returning the ids
 * dropped.
 *
 * Needed only because the state now outlives the process. While the manager is
 * up, an unassign/pause/revoke reaches `forgetChannelSyncState` and the entry
 * goes with it. An agent moved off the host while the manager was DOWN has no
 * such moment: its entry would rehydrate, be reported on every heartbeat, and
 * be rejected by the API's assignment check — a warning per heartbeat forever,
 * for an agent nobody is syncing. Persisting without this would trade a daily
 * false recovery for permanent log noise.
 */
export function pruneStuckStreaksToAgents(
  maps: StuckStreakMaps,
  activeAgentIds: ReadonlySet<string>,
): string[] {
  const dropped: string[] = [];
  for (const concern of STUCK_STREAK_CONCERNS) {
    for (const agentId of [...maps[concern].keys()]) {
      if (activeAgentIds.has(agentId)) continue;
      maps[concern].delete(agentId);
      if (!dropped.includes(agentId)) dropped.push(agentId);
    }
  }
  return dropped;
}
